import { type NFTCollectibleDefinition, useMeFavouritePartner } from '@capsicum/common';
import { useCallback } from 'react';

import { useOngoingUserParticipationsQuery } from '../../locker-room/data-access/queries/useOngoingUserParticipationsQuery';
import { type LockerRoomCampaignReward, type LockerRoomUnlock } from '../../locker-room/models/LockerRoomOptionType';
import { usePartners } from '../../partners/hooks/usePartners';
import { type MyNft } from '../models/myNfts';
import { mapPartnerToNftPartner } from '../util/mapPartnerToNftPartner';

/**
 * @returns combined sorted list of nfts owned by user and LockerRoom participations. Ongoing participations are displayed with an overlay.
 */
export const useMyOngoingParticipationsNfts = () => {
	const { getPartnerBy } = usePartners();
	const { favoritePartner } = useMeFavouritePartner();

	return useOngoingUserParticipationsQuery(
		useCallback(
			(data: Array<LockerRoomUnlock> | undefined) =>
				data?.reduce(
					(sortedNfts, currentParticipation) => {
						if (currentParticipation?.status === 'accepted') {
							const stackingClosedAt = currentParticipation?.challenge?.campaign?.stackingClosedAt || '';
							const reward: LockerRoomCampaignReward | undefined = currentParticipation.rewards?.[0];
							const partner = mapPartnerToNftPartner(
								getPartnerBy(p => p.id === reward?.reward?.nft?.summary?.partnerId)
							);
							const rewardNft = reward?.reward?.nft as NFTCollectibleDefinition;

							const myNft = {
								optionType: currentParticipation.optionType,
								partner,
								summary: {
									...rewardNft.summary,
									nftId: reward?.id
								},
								unlockingDate: stackingClosedAt,
								userParticipationId: currentParticipation.id
							} as MyNft;

							if (partner && partner.id === favoritePartner?.id) {
								sortedNfts.favorite.push(myNft);
							} else {
								sortedNfts.other.push(myNft);
							}
						}

						return sortedNfts;
					},
					{
						favorite: [],
						other: []
					} as { favorite: Array<MyNft>; other: Array<MyNft> }
				),
			[favoritePartner?.id, getPartnerBy]
		),
		{
			direction: 'desc',
			orderBy: 'createdAt',
			'rewardTypes[]': ['nft']
		}
	);
};
